import axios from 'axios';

const getEnv = (key) => {
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }
  return import.meta.env[key];
};

const BASE_URL = getEnv('VITE_API_URL');

// Configurar axios com timeout maior para requisições pesadas
const axiosInstance = axios.create({
  timeout: 180000, // 120 segundos para batch
});

// Interceptor para adicionar token de autenticação do painel administrativo
axiosInstance.interceptors.request.use(
  (config) => {
    // Buscar token do localStorage (mesmo token usado no painel-looker)
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Se receber 401 (não autorizado), redirecionar para login do painel
    if (error.response?.status === 401) {
      // Limpar token inválido
      localStorage.removeItem('authToken');
      
      // Redirecionar para o painel administrativo para fazer login
      const painelUrl = getEnv('VITE_PAINEL_URL') ?? 'http://localhost:5173';
      window.location.href = `${painelUrl}/login?redirect=${encodeURIComponent(window.location.href)}`;
    }
    return Promise.reject(error);
  }
);

// ============================================
// Sistema de Cache + Deduplicação (30 minutos)
// ============================================
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos

class OptimizedCache {
  constructor() {
    this.cache = new Map();
    this.inflight = new Map();
    this.stats = { hits: 0, misses: 0, deduplicated: 0, batchRequests: 0 };
  }

  // Stringify estável para maximizar hit rate do cache (ordena chaves recursivamente)
  stableStringify(value) {
    const seen = new WeakSet();
    const normalize = (v) => {
      if (v === undefined) return undefined;
      if (v === null) return null;
      if (typeof v !== 'object') return v;
      if (seen.has(v)) return undefined; // evitar ciclos
      seen.add(v);

      if (Array.isArray(v)) {
        return v.map(normalize);
      }

      const out = {};
      Object.keys(v)
        .sort()
        .forEach((k) => {
          const nv = normalize(v[k]);
          if (nv !== undefined) out[k] = nv;
        });
      return out;
    };
    return JSON.stringify(normalize(value));
  }

  generateKey(lookId, filters, extra) {
    return this.stableStringify({ lookId, filters, extra });
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) {
      this.stats.misses++;
      return null;
    }

    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }

    this.stats.hits++;
    return entry.data;
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      expiry: Date.now() + CACHE_DURATION,
    });
  }

  getInflight(key) {
    return this.inflight.get(key);
  }

  setInflight(key, promise) {
    this.inflight.set(key, promise);
  }

  removeInflight(key) {
    this.inflight.delete(key);
  }

  clear() {
    this.cache.clear();
    this.inflight.clear();
  }

  getStats() {
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? ((this.stats.hits / total) * 100).toFixed(2) : 0;
    return {
      ...this.stats,
      cacheSize: this.cache.size,
      inflightSize: this.inflight.size,
      hitRate: `${hitRate}%`
    };
  }
}

const apiCache = new OptimizedCache();

// Função para limpar cache (frontend e backend)
export async function clearCache() {
  // Limpar cache do frontend
  apiCache.clear();
  
  // Limpar cache do backend
  try {
    await axiosInstance.delete(`${BASE_URL}/cache/clear`);
  } catch (error) {
    console.error('Erro ao limpar cache do backend:', error);
    // Não falhar se o backend não responder - o cache do frontend já foi limpo
  }
}

// ============================================
// BATCH REQUEST - Uma requisição para múltiplos looks
// ============================================
async function fetchBatch(requests) {
  // Separar requisições em cache e não-cache
  const cached = [];
  const toFetch = [];
  const inflightByIndex = [];
  const deferredByCacheKey = new Map();

  const createDeferred = () => {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };

  for (let i = 0; i < requests.length; i++) {
    const req = requests[i];
    const cacheKey = apiCache.generateKey(req.lookId, req.filters || {}, req.extra || {});
    const cachedData = apiCache.get(cacheKey);
    if (cachedData !== null) {
      cached.push({ index: i, data: cachedData, fromCache: true });
    } else {
      // Se já existe requisição em andamento para esse cacheKey, reaproveitar
      const inflight = apiCache.getInflight(cacheKey);
      if (inflight) {
        apiCache.stats.deduplicated++;
        inflightByIndex.push({ index: i, promise: inflight });
      } else {
        toFetch.push({ ...req, originalIndex: i, cacheKey });
      }
    }
  }
  
  // Se tudo está em cache, retornar imediatamente
  if (toFetch.length === 0) {
    const resultPromises = requests.map((_, i) => {
      const cachedItem = cached.find(c => c.index === i);
      if (cachedItem) return Promise.resolve(cachedItem.data);
      const inflightItem = inflightByIndex.find(p => p.index === i);
      if (inflightItem) return inflightItem.promise;
      return Promise.resolve([]);
    });
    return Promise.all(resultPromises);
  }
  
  // Marcar cada cacheKey individual como "inflight" para deduplicar com fetchLook()
  // Filtrar toFetch para remover requests que já estão inflight
  const toFetchFiltered = [];
  for (const req of toFetch) {
    const existingInflight = apiCache.getInflight(req.cacheKey);
    if (existingInflight) {
      // Já existe uma requisição em andamento - aguardar ela
      apiCache.stats.deduplicated++;
      inflightByIndex.push({ index: req.originalIndex, promise: existingInflight });
    } else {
      // Criar deferred para este cacheKey
      const d = createDeferred();
      deferredByCacheKey.set(req.cacheKey, d);
      apiCache.setInflight(req.cacheKey, d.promise);
      toFetchFiltered.push(req);
    }
  }
  
  // Se não há nada para buscar (tudo está inflight), retornar aguardando promises
  if (toFetchFiltered.length === 0) {
    const resultPromises = requests.map((_, i) => {
      const cachedItem = cached.find(c => c.index === i);
      if (cachedItem) return Promise.resolve(cachedItem.data);
      const inflightItem = inflightByIndex.find(p => p.index === i);
      if (inflightItem) return inflightItem.promise;
      return Promise.resolve([]);
    });
    return Promise.all(resultPromises);
  }
  
  // Verificar se já existe batch em andamento com as mesmas requisições (após filtrar inflight)
  // Usar lookId + filters + page + limit para criar uma chave estável
  const batchKey = JSON.stringify(
    toFetchFiltered.map(r => ({
      lookId: r.lookId,
      filters: r.filters || {},
      page: r.extra?.page || 1,
      limit: r.extra?.limit || 100,
    })).sort((a, b) => {
      if (a.lookId !== b.lookId) return a.lookId - b.lookId;
      const aKey = JSON.stringify(a.filters);
      const bKey = JSON.stringify(b.filters);
      if (aKey !== bKey) return aKey.localeCompare(bKey);
      if (a.page !== b.page) return a.page - b.page;
      return a.limit - b.limit;
    })
  );
  const inflightBatch = apiCache.getInflight(batchKey);
  if (inflightBatch) {
    apiCache.stats.deduplicated++;
    return inflightBatch;
  }
  
  // Fazer requisição batch para o backend (usando apenas requests não inflight)
  const batchPromise = (async () => {
    try {
      apiCache.stats.batchRequests++;
      
      const batchPayload = toFetchFiltered.map(req => ({
        lookId: req.lookId,
        filters: req.filters || {},
        page: req.extra?.page || 1,
        limit: req.extra?.limit || 100,
      }));
      
      const { data } = await axiosInstance.post(`${BASE_URL}/processes/batch`, {
        requests: batchPayload
      });
      
      // Processar resultados e salvar no cache
      const resultsMap = new Map();
      if (data?.results) {
        data.results.forEach((result, idx) => {
          const req = toFetchFiltered[idx];
          const resultData = result?.data || [];
          
          // Salvar no cache individual
          apiCache.set(req.cacheKey, resultData);
          // Resolver inflight individual
          const d = deferredByCacheKey.get(req.cacheKey);
          if (d) d.resolve(resultData);
          resultsMap.set(req.originalIndex, resultData);
          // Remover inflight individual
          apiCache.removeInflight(req.cacheKey);
        });
      }
      
      // Montar resultado final na ordem original
      const finalResults = requests.map((_, i) => {
        const cachedItem = cached.find(c => c.index === i);
        if (cachedItem) return cachedItem.data;
        const inflightItem = inflightByIndex.find(p => p.index === i);
        if (inflightItem) return null; // será preenchido abaixo após await
        return resultsMap.get(i) || [];
      });

      // Resolver itens que estavam inflight (aguardar)
      for (const item of inflightByIndex) {
        // eslint-disable-next-line no-await-in-loop
        finalResults[item.index] = await item.promise;
      }

      return finalResults;
      
    } catch (error) {
      console.error('Erro no batch request:', error);
      // Resolver inflight individual com fallback vazio
      for (const req of toFetchFiltered) {
        const d = deferredByCacheKey.get(req.cacheKey);
        if (d) d.resolve([]);
        apiCache.removeInflight(req.cacheKey);
      }
      // Fallback: retornar arrays vazios
      return requests.map(() => []);
    }
  })();
  
  apiCache.setInflight(batchKey, batchPromise);
  batchPromise.finally(() => apiCache.removeInflight(batchKey));
  
  return batchPromise;
}

// Função individual (usa batch internamente quando possível)
async function fetchLook(lookId, filters = {}, extra = {}) {
  const cacheKey = apiCache.generateKey(lookId, filters, extra);
  
  // Verificar cache
  const cachedData = apiCache.get(cacheKey);
  if (cachedData !== null) {
    return cachedData;
  }
  
  // Verificar requisição em andamento
  const inflightPromise = apiCache.getInflight(cacheKey);
  if (inflightPromise) {
    apiCache.stats.deduplicated++;
    return inflightPromise;
  }

  // Requisição individual (para componentes isolados)
  const requestPromise = (async () => {
    try {
      const params = { lookId, ...filters, ...extra };
      const { data } = await axiosInstance.get(`${BASE_URL}/processes`, { params });
      const result = data?.data || [];
      apiCache.set(cacheKey, result);
      return result;
    } catch (error) {
      return [];
    }
  })();

  apiCache.setInflight(cacheKey, requestPromise);
  requestPromise.finally(() => apiCache.removeInflight(cacheKey));
  
  return requestPromise;
}

export const LOOKS = {
  // Filtros 
  posto: 87,
  produto: 88,
  estado: 89,
  cidade: 90,
  funcionario: 91,

  // Tela Visão Geral
  faturamento: 9,
  fat_projecao: 94,
  lucro: 11,
  lucro_projecao: 96,
  margem_lucro:101,
  custo:13,
  custo_projecao: 143,
  litros: 15,
  litros_projecao: 144,
  litros_volume:99,
  abastecimento: 16,
  pico_abastecimento: 100,
  postos: 98,
  lucro_postos: 117,
  media_lucro_postos: 118,
  formas_pg_usadas: 111,
  abastecimento_turno: 112,
  abastecimento_projecao: 145,
  indicadores: 129,
  mapa: 135,
  litros_produto: 136,
  tabela_indicadores: 141,

  // Tela Evolução de Vendas
  comparativo_anual: 146,
  combustivelLitros: 147,
};

// Função específica para buscar dados do gráfico de evolução
// Parâmetros:
// - indicador: 'Faturamento', 'Lucro', 'Custo', 'Litros', 'Abastecimento'
// - periodo: 'Dia', 'Semana', 'Mês'
export async function getEvolucaoIndicadores(filters = {}, indicador = 'Faturamento', periodo = 'Dia') {
  // VALIDAÇÃO: Não fazer requisição se não houver filtro de data
  if (!filters['visao_geral.data_hora_abastecimento_sp_date']) {
    // Evitar poluir o console (especialmente no mount inicial antes do filtro setar datas)
    if (import.meta.env.DEV && import.meta.env.VITE_LOG_BLOCKED_REQUESTS === 'true') {
      console.warn('[getEvolucaoIndicadores] Requisição bloqueada: filtro de data não encontrado');
    }
    return [];
  }
  
  const filtrosComIndicadores = {
    ...filters,
    'visao_geral.indicador': indicador,
    'visao_geral.indicador_mes_dia_semana': periodo,
  };
  
  const dados = await fetchLook(LOOKS.indicadores, filtrosComIndicadores, { page: 1, limit: 100 });
  return dados;
}

// Função específica para buscar dados do comparativo anual
// Parâmetros:
// - indicador: 'faturamento', 'lucro', 'litros' (mesmo formato do getEvolucaoIndicadores)
export async function getComparativoAnual(filters = {}, indicador = 'faturamento') {
  const filtrosComIndicador = {
    ...filters,
    'visao_geral.indicador': indicador,
  };
  const dados = await fetchLook(LOOKS.comparativo_anual, filtrosComIndicador, { page: 1, limit: 100 });
  return dados;
}

// Função específica para buscar dados do mapa
// Parâmetros:
// - indicador: 'faturamento', 'lucro', 'custo', 'litros', 'abastecimentos'
export async function getDadosMapa(filters = {}, indicador = 'faturamento') {
  const filtrosComIndicador = {
    ...filters,
    'visao_geral.indicador': indicador,
  };
  
  const dados = await fetchLook(LOOKS.mapa, filtrosComIndicador, { page: 1, limit: 500 });
  return dados;
}

// Função específica para buscar dados de litros por produto
export async function getLitrosPorProduto(filters = {}) {
  // VALIDAÇÃO: Não fazer requisição se não houver filtro de data
  if (!filters['visao_geral.data_hora_abastecimento_sp_date']) {
    if (import.meta.env.DEV && import.meta.env.VITE_LOG_BLOCKED_REQUESTS === 'true') {
      console.warn('[getLitrosPorProduto] Requisição bloqueada: filtro de data não encontrado');
    }
    return [];
  }
  
  const dados = await fetchLook(LOOKS.litros_produto, filters, { page: 1, limit: 100 });
  return dados;
}

// Função específica para buscar dados da tabela de indicadores (posto, produto, funcionario)
// Parâmetros:
// - indicador: 'posto', 'produto', 'funcionario'
export async function getTabelaIndicadores(filters = {}, indicador = 'posto') {
  // VALIDAÇÃO: Não fazer requisição se não houver filtro de data
  if (!filters['visao_geral.data_hora_abastecimento_sp_date']) {
    if (import.meta.env.DEV && import.meta.env.VITE_LOG_BLOCKED_REQUESTS === 'true') {
      console.warn('[getTabelaIndicadores] Requisição bloqueada: filtro de data não encontrado');
    }
    return [];
  }
  
  const filtrosComIndicador = {
    ...filters,
    'visao_geral.indicador_posto_produto_fun': indicador,
  };
  
  const dados = await fetchLook(LOOKS.tabela_indicadores, filtrosComIndicador, { page: 1, limit: 100 });
  return dados;
}

// Função específica para buscar dados dos cards de combustível
export async function getCards(filters = {}) {
  const dados = await fetchLook(LOOKS.combustivelLitros, filters, { page: 1, limit: 100 });
  return dados;
}

// Função específica para buscar total de postos (evita chamar getVisaoGeral completo)
export async function getTotalPostos(filters = {}) {
  const dados = await fetchLook(LOOKS.postos, filters, { page: 1, limit: 1 });
  if (!Array.isArray(dados) || dados.length === 0) return null;
  const item = dados[0] || {};
  const total =
    item['visao_geral.diferentes_postos'] ??
    item.diferentes_postos ??
    item['visao_geral.total_postos'] ??
    item.total_postos ??
    null;
  return total === null ? null : Number(total);
}

export async function getFiltros(filters = {}) {
  // Usar BATCH para buscar todos os filtros em UMA requisição
  const batchRequests = [
    { lookId: LOOKS.posto, filters, extra: { page: 1, limit: 150 } },
    { lookId: LOOKS.produto, filters },
    { lookId: LOOKS.estado, filters },
    { lookId: LOOKS.cidade, filters },
    { lookId: LOOKS.funcionario, filters, extra: { page: 1, limit: 5000 } },
  ];

  const results = await fetchBatch(batchRequests);

  return {
    filtro_posto: results[0] || [],
    filtro_produto: results[1] || [],
    filtro_estado: results[2] || [],
    filtro_cidade: results[3] || [],
    filtro_funcionario: results[4] || [],
  };
}

export async function getVisaoGeral(filters = {}, page = 1, limit = 500) {
  // Configurar extras
  const extraSimples = { page: 1, limit: 1 };
  const extra100 = { page: 1, limit: 100 };
  const extra = { page, limit };

  // BATCH REQUEST - Uma única requisição HTTP para todos os looks
  const batchRequests = [
    { lookId: LOOKS.faturamento, filters, extra: extraSimples },
    { lookId: LOOKS.fat_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.lucro, filters, extra: extraSimples },
    { lookId: LOOKS.lucro_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.margem_lucro, filters, extra: extraSimples },
    { lookId: LOOKS.custo, filters, extra: extraSimples },
    { lookId: LOOKS.custo_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.litros, filters, extra: extraSimples },
    { lookId: LOOKS.litros_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.litros_volume, filters, extra: extra100 },
    { lookId: LOOKS.abastecimento, filters, extra: extraSimples },
    { lookId: LOOKS.pico_abastecimento, filters, extra: extra100 },
    { lookId: LOOKS.postos, filters, extra: extraSimples },
    { lookId: LOOKS.lucro_postos, filters, extra },
    { lookId: LOOKS.media_lucro_postos, filters, extra: extraSimples },
    { lookId: LOOKS.formas_pg_usadas, filters, extra },
    { lookId: LOOKS.abastecimento_turno, filters, extra: extra100 },
    { lookId: LOOKS.abastecimento_projecao, filters, extra: extraSimples },
  ];

  const results = await fetchBatch(batchRequests);

  return {
    visao_geral_faturamento: results[0] || [],
    visao_geral_fat_projecao: results[1] || [],
    visao_geral_lucro: results[2] || [],
    visao_geral_lucro_projecao: results[3] || [],
    visao_geral_margem_lucro: results[4] || [],
    visao_geral_custo: results[5] || [],
    visao_geral_custo_projecao: results[6] || [],
    visao_geral_litros: results[7] || [],
    visao_geral_litros_projecao: results[8] || [],
    visao_geral_litros_volume: results[9] || [],
    visao_geral_abastecimento: results[10] || [],
    visao_geral_pico_abastecimento: results[11] || [],
    visao_geral_postos: results[12] || [],
    visao_geral_lucro_postos: results[13] || [],
    visao_geral_media_lucro_postos: results[14] || [],
    visao_geral_formas_pg_usadas: results[15] || [],
    visao_geral_abastecimento_turno: results[16] || [],
    visao_geral_abastecimento_projecao: results[17] || [],
  };
}

// ============================================
// KPI REQUEST (leve) - para carregar rápido (cards)
// ============================================
export async function getVisaoGeralKpis(filters = {}) {
  const extra1 = { page: 1, limit: 1 };
  const extra100 = { page: 1, limit: 100 };

  const extra500 = { page: 1, limit: 500 };
  const batchRequests = [
    { lookId: LOOKS.faturamento, filters, extra: extra1 },
    { lookId: LOOKS.fat_projecao, filters, extra: extra1 },
    { lookId: LOOKS.lucro, filters, extra: extra1 },
    { lookId: LOOKS.lucro_projecao, filters, extra: extra1 },
    { lookId: LOOKS.margem_lucro, filters, extra: extra1 },
    { lookId: LOOKS.custo, filters, extra: extra1 },
    { lookId: LOOKS.custo_projecao, filters, extra: extra1 },
    { lookId: LOOKS.litros, filters, extra: extra1 },
    { lookId: LOOKS.litros_projecao, filters, extra: extra1 },
    { lookId: LOOKS.litros_volume, filters, extra: extra100 },
    { lookId: LOOKS.abastecimento, filters, extra: extra1 },
    { lookId: LOOKS.abastecimento_projecao, filters, extra: extra1 },
    { lookId: LOOKS.pico_abastecimento, filters, extra: extra100 },
    { lookId: LOOKS.postos, filters, extra: extra1 },
    { lookId: LOOKS.media_lucro_postos, filters, extra: extra1 },
    { lookId: LOOKS.formas_pg_usadas, filters, extra: extra500 },
    { lookId: LOOKS.abastecimento_turno, filters, extra: extra100 },
  ];

  // Verificar se todos os dados já estão no cache (otimização)
  const allCached = batchRequests.every(req => {
    const cacheKey = apiCache.generateKey(req.lookId, req.filters || {}, req.extra || {});
    return apiCache.get(cacheKey) !== null;
  });

  // Se tudo está em cache, retornar imediatamente sem fazer batch
  let results;
  if (allCached) {
    results = await Promise.all(
      batchRequests.map(req => fetchLook(req.lookId, req.filters || {}, req.extra || {}))
    );
  } else {
    results = await fetchBatch(batchRequests);
  }

  return {
    visao_geral_faturamento: results[0] || [],
    visao_geral_fat_projecao: results[1] || [],
    visao_geral_lucro: results[2] || [],
    visao_geral_lucro_projecao: results[3] || [],
    visao_geral_margem_lucro: results[4] || [],
    visao_geral_custo: results[5] || [],
    visao_geral_custo_projecao: results[6] || [],
    visao_geral_litros: results[7] || [],
    visao_geral_litros_projecao: results[8] || [],
    visao_geral_litros_volume: results[9] || [],
    visao_geral_abastecimento: results[10] || [],
    visao_geral_abastecimento_projecao: results[11] || [],
    visao_geral_pico_abastecimento: results[12] || [],
    visao_geral_postos: results[13] || [],
    visao_geral_media_lucro_postos: results[14] || [],
    visao_geral_formas_pg_usadas: results[15] || [],
    visao_geral_abastecimento_turno: results[16] || [],
    // campos não-kpi ficam vazios (mantém compatibilidade do processor)
    visao_geral_lucro_postos: [],
  };
}

// ============================================
// PREFETCH - Visão Geral (1 request por página)
// ============================================
function buildVisaoGeralRequests(filters = {}, page = 1, limit = 500) {
  const extraSimples = { page: 1, limit: 1 };
  const extra100 = { page: 1, limit: 100 };
  const extra = { page, limit };

  return [
    { lookId: LOOKS.faturamento, filters, extra: extraSimples },
    { lookId: LOOKS.fat_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.lucro, filters, extra: extraSimples },
    { lookId: LOOKS.lucro_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.margem_lucro, filters, extra: extraSimples },
    { lookId: LOOKS.custo, filters, extra: extraSimples },
    { lookId: LOOKS.custo_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.litros, filters, extra: extraSimples },
    { lookId: LOOKS.litros_projecao, filters, extra: extraSimples },
    { lookId: LOOKS.litros_volume, filters, extra: extra100 },
    { lookId: LOOKS.abastecimento, filters, extra: extraSimples },
    { lookId: LOOKS.pico_abastecimento, filters, extra: extra100 },
    { lookId: LOOKS.postos, filters, extra: extraSimples },
    { lookId: LOOKS.lucro_postos, filters, extra },
    { lookId: LOOKS.media_lucro_postos, filters, extra: extraSimples },
    { lookId: LOOKS.formas_pg_usadas, filters, extra },
    { lookId: LOOKS.abastecimento_turno, filters, extra: extra100 },
    { lookId: LOOKS.abastecimento_projecao, filters, extra: extraSimples },
  ];
}

function calcularMesAnteriorCompletoAPIFilters(filtersApi = {}) {
  const filtroData = filtersApi['visao_geral.data_hora_abastecimento_sp_date'];
  if (!filtroData || typeof filtroData !== 'string') return null;

  let dataInicio = filtroData;
  if (filtroData.includes(' to ')) {
    [dataInicio] = filtroData.split(' to ').map(d => d.trim());
  } else if (filtroData.startsWith('>=')) {
    dataInicio = filtroData.replace('>=', '').trim();
  } else if (filtroData.startsWith('<=')) {
    dataInicio = filtroData.replace('<=', '').trim();
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(dataInicio)) return null;
  const dataAtual = new Date(`${dataInicio}T00:00:00`);
  if (Number.isNaN(dataAtual.getTime())) return null;

  const mesAnterior = new Date(dataAtual);
  mesAnterior.setMonth(mesAnterior.getMonth() - 1);
  const inicioAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth(), 1);
  const fimAnterior = new Date(mesAnterior.getFullYear(), mesAnterior.getMonth() + 1, 0);

  const inicioStr = inicioAnterior.toISOString().split('T')[0];
  const fimStr = fimAnterior.toISOString().split('T')[0];

  return {
    ...filtersApi,
    'visao_geral.data_hora_abastecimento_sp_date': `${inicioStr} to ${fimStr}`,
  };
}

/**
 * Prefetch agressivo da Visão Geral em UMA única chamada batch.
 * Isso aquece o cache para que componentes que ainda chamam APIs individualmente
 * retornem instantaneamente (cache hit / inflight dedupe).
 */
export async function prefetchVisaoGeralDashboard({
  filtersCurrent = {},
  filtersComparison = null,
  page = 1,
  limit = 500,
  prefetchAllInteractiveCombos = true,
  includeVisaoGeralBundle = false,
  includeKpis = false, // Novo: incluir KPIs no prefetch para evitar chamadas do hook
} = {}) {
  const requests = [];

  // Opcional: bundle completo da Visão Geral (caro). KPIs já são buscados por getVisaoGeralKpis.
  if (includeVisaoGeralBundle) {
    requests.push(...buildVisaoGeralRequests(filtersCurrent, page, limit));
    if (filtersComparison) {
      requests.push(...buildVisaoGeralRequests(filtersComparison, page, limit));
    }
  }

  // Incluir KPIs no prefetch (stage 1) para que o hook encontre tudo no cache
  if (includeKpis) {
    const extra1 = { page: 1, limit: 1 };
    const extra100 = { page: 1, limit: 100 };
    const extra500 = { page: 1, limit: 500 };
    const kpiLookIds = [
      LOOKS.faturamento, LOOKS.fat_projecao, LOOKS.lucro, LOOKS.lucro_projecao,
      LOOKS.margem_lucro, LOOKS.custo, LOOKS.custo_projecao, LOOKS.litros,
      LOOKS.litros_projecao, LOOKS.litros_volume, LOOKS.abastecimento,
      LOOKS.abastecimento_projecao, LOOKS.pico_abastecimento, LOOKS.postos,
      LOOKS.media_lucro_postos, LOOKS.formas_pg_usadas, LOOKS.abastecimento_turno,
    ];
    kpiLookIds.forEach(lookId => {
      let extra = extra1;
      if (lookId === LOOKS.litros_volume || lookId === LOOKS.pico_abastecimento || lookId === LOOKS.abastecimento_turno) {
        extra = extra100;
      } else if (lookId === LOOKS.formas_pg_usadas) {
        extra = extra500;
      }
      requests.push({ lookId, filters: filtersCurrent, extra });
    });
    if (filtersComparison) {
      kpiLookIds.forEach(lookId => {
        let extra = extra1;
        if (lookId === LOOKS.litros_volume || lookId === LOOKS.pico_abastecimento || lookId === LOOKS.abastecimento_turno) {
          extra = extra100;
        } else if (lookId === LOOKS.formas_pg_usadas) {
          extra = extra500;
        }
        requests.push({ lookId, filters: filtersComparison, extra });
      });
    }
  }

  const hasDate = !!filtersCurrent?.['visao_geral.data_hora_abastecimento_sp_date'];
  if (hasDate) {
    // Stage 1 (crítico): APENAS o essencial para render inicial
    // - 1 combo de cada gráfico (o que está visível por padrão)
    // - Não pré-carregar todos os combos (isso vai para stage 2)
    
    // Gráfico Evolução (look 129) - apenas o padrão (litros/dia)
    requests.push({
      lookId: LOOKS.indicadores,
      filters: {
        ...filtersCurrent,
        'visao_geral.indicador': 'litros',
        'visao_geral.indicador_mes_dia_semana': 'dia',
      },
      extra: { page: 1, limit: 100 },
    });

    // Mapa (look 135) - apenas o padrão (faturamento)
    requests.push({
      lookId: LOOKS.mapa,
      filters: { ...filtersCurrent, 'visao_geral.indicador': 'faturamento' },
      extra: { page: 1, limit: 500 },
    });

    // Tabela (look 141) - apenas o padrão (posto)
    requests.push({
      lookId: LOOKS.tabela_indicadores,
      filters: { ...filtersCurrent, 'visao_geral.indicador_posto_produto_fun': 'posto' },
      extra: { page: 1, limit: 100 },
    });

    // Litros por produto (look 136) - atual + mês anterior
    requests.push({
      lookId: LOOKS.litros_produto,
      filters: filtersCurrent,
      extra: { page: 1, limit: 100 },
    });
    const filtersMesAnterior = calcularMesAnteriorCompletoAPIFilters(filtersCurrent);
    if (filtersMesAnterior) {
      requests.push({
        lookId: LOOKS.litros_produto,
        filters: filtersMesAnterior,
        extra: { page: 1, limit: 100 },
      });
    }

    // Stage 2: Se prefetchAllInteractiveCombos, adicionar todos os combos (roda depois)
    if (prefetchAllInteractiveCombos) {
      const indicadores = ['litros', 'lucro', 'faturamento'];
      const periodos = ['dia', 'semana', 'mes'];
      const evolucaoCombos = indicadores.flatMap(ind => periodos.map(per => ({ ind, per })));
      
      // Remover o combo já adicionado (litros/dia)
      evolucaoCombos.filter(({ ind, per }) => !(ind === 'litros' && per === 'dia')).forEach(({ ind, per }) => {
        requests.push({
          lookId: LOOKS.indicadores,
          filters: {
            ...filtersCurrent,
            'visao_geral.indicador': ind,
            'visao_geral.indicador_mes_dia_semana': per,
          },
          extra: { page: 1, limit: 100 },
        });
      });

      // Mapa - remover faturamento já adicionado
      ['lucro', 'custo', 'litros', 'abastecimentos'].forEach((ind) => {
        requests.push({
          lookId: LOOKS.mapa,
          filters: { ...filtersCurrent, 'visao_geral.indicador': ind },
          extra: { page: 1, limit: 500 },
        });
      });

      // Tabela - remover posto já adicionado
      ['produto', 'funcionario'].forEach((ind) => {
        requests.push({
          lookId: LOOKS.tabela_indicadores,
          filters: { ...filtersCurrent, 'visao_geral.indicador_posto_produto_fun': ind },
          extra: { page: 1, limit: 100 },
        });
      });
    }
  }

  // Executa o batch - resultado não é usado aqui; o objetivo é aquecer cache
  if (requests.length > 0) {
    await fetchBatch(requests);
  }
  return true;
}